import { Link } from "react-router-dom";
import { HeartHandshake, Phone, Mail, MapPin, MessageCircle, Facebook, Instagram, Twitter, Linkedin, Youtube } from "lucide-react";
import { useApp } from "../context/AppContext";

export default function Footer() {
  const { t, site } = useApp();
  const s = site?.settings || {};
  const social = s.social || {};
  const socials = [
    ["facebook", Facebook, social.facebook], ["instagram", Instagram, social.instagram],
    ["x", Twitter, social.x], ["linkedin", Linkedin, social.linkedin], ["youtube", Youtube, social.youtube],
  ].filter(([, , url]) => url);

  return (
    <footer data-testid="site-footer" className="grain bg-navy text-slate-300">
      <div className="container-ssf grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white p-0.5 ring-2 ring-saffron-500/60">
              <img src="/logo.png" alt="Sahaj Sarathi Foundation logo" className="h-full w-full rounded-full object-contain" />
            </span>
            <div className="leading-tight">
              <p className="font-display text-lg font-bold text-white">Sahaj Sarathi Foundation</p>
              <p className="text-[11px] text-slate-400">Registered NGO</p>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">{lang_text(t, s)}</p>
        </div>
        <nav aria-label="Footer quick links">
          <h3 className="label-eyebrow">{t("quick_links")}</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            {(s.footer?.quick_links?.length ? s.footer.quick_links : [
              { label_en: t("about"), label_hi: "", url: "/about" }, { label_en: t("work"), label_hi: "", url: "/work" },
              { label_en: t("projects"), label_hi: "", url: "/projects" }, { label_en: t("articles"), label_hi: "", url: "/articles" },
              { label_en: t("gallery"), label_hi: "", url: "/gallery" }, { label_en: t("greetings"), label_hi: "", url: "/greetings" },
              { label_en: t("news"), label_hi: "", url: "/news" }, { label_en: t("register"), label_hi: "", url: "/register" },
              { label_en: t("verify_cert"), label_hi: "", url: "/verify" }, { label_en: t("contact"), label_hi: "", url: "/contact" },
            ]).map((l, i) => (
              <li key={l.url + i}><Link className="transition-colors hover:text-saffron-500" to={l.url} data-testid={`footer-link-${i}`}>{lang_text_l(l)}</Link></li>
            ))}
            <li><Link className="transition-colors hover:text-saffron-500" to="/print-receipt" data-testid="footer-print-receipt">Print Receipt</Link></li>
            <li><Link className="transition-colors hover:text-saffron-500" to="/donate/check-status" data-testid="footer-check-status">Check Payment Status</Link></li>
          </ul>
        </nav>
        <div>
          <h3 className="label-eyebrow">{t("recognition")}</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
            <li>UDYAM: {s.udyam || "UDYAM-UP-03-0051126"}</li>
            <li>NGO DARPAN: {s.darpan_id || "UP/2012/03384132"}</li>
            <li className="text-xs leading-relaxed">{s.divyangjan_cert || "5826/R/OL/2023-24 — Divyangjan Empowerment Department, Govt. of UP"}</li>
            {s.tax_12a && <li>12A: {s.tax_12a}</li>}
            {s.tax_80g && <li>80G: {s.tax_80g}</li>}
          </ul>
        </div>
        <div>
          <h3 className="label-eyebrow">{t("contact_info")}</h3>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start gap-2.5"><MapPin size={16} className="mt-0.5 shrink-0 text-saffron-500" />{s.address_line || "Prayagraj, Uttar Pradesh - 211015"}</li>
            <li><a className="flex items-center gap-2.5 transition-colors hover:text-saffron-500" href={`tel:${(s.phone || "").replace(/\s/g, "")}`} data-testid="footer-phone"><Phone size={16} className="shrink-0 text-saffron-500" />{s.phone || "+91 7897477893"}</a></li>
            <li><a className="flex items-center gap-2.5 transition-colors hover:text-saffron-500" href={`mailto:${s.email || "ssf@sahajsarathi.com"}`} data-testid="footer-email"><Mail size={16} className="shrink-0 text-saffron-500" />{s.email || "ssf@sahajsarathi.com"}</a></li>
            <li><a className="flex items-center gap-2.5 transition-colors hover:text-saffron-500" href={`https://wa.me/${s.whatsapp || "917897477893"}`} target="_blank" rel="noreferrer" data-testid="footer-whatsapp"><MessageCircle size={16} className="shrink-0 text-saffron-500" />WhatsApp</a></li>
          </ul>
          {socials.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("follow_us")}</p>
              <div className="mt-2 flex gap-2">
                {socials.map(([name, Icon, url]) => (
                  <a key={name} href={url} target="_blank" rel="noreferrer" aria-label={name} data-testid={`social-${name}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-saffron-500 hover:text-navy">
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-white/10 py-5">
        <div className="container-ssf flex flex-col items-center justify-between gap-2 text-xs text-slate-500 sm:flex-row">
          <p>{s.footer?.copyright || `© ${new Date().getFullYear()} Sahaj Sarathi Foundation. ${t("rights")}`}</p>
          <Link to="/superadmin/login" className="transition-colors hover:text-saffron-500" data-testid="footer-staff-login">Superadmin</Link>
        </div>
        {(s.footer?.disclaimer || s.footer?.privacy || s.footer?.terms) && (
          <div className="mt-2 text-center text-[11px] text-slate-500 sm:text-left">
            {[s.footer?.disclaimer, s.footer?.privacy, s.footer?.terms].filter(Boolean).join("  •  ")}
          </div>
        )}
      </div>
    </footer>
  );
}

function lang_text(t, s) {
  return s.tagline_en || "Empowering lives through education, health, social security and equal participation.";
}

function lang_text_l(l) {
  return l.label_en;
}
